import React from "react";
import * as SubframeCore from "@subframe/core";
import { ExitSurveyRow, ExitSurveyFilter, ExitSurveyStatus } from "modules/Retention/types/exitSurvey";
import { Badge } from "subframe";
import { ExitSurveyTableSkeleton } from "./ExitSurveyTableSkeleton";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCol,
  TableHeader,
  TableActionCell
} from "common/components/Table";
import { Tag } from "subframe/components/Tag";
import RETENTION_ROUTES, { getEmployeeProfileRoute } from "modules/Retention/constants/routes";
import { useNavigate } from "react-router-dom";
import { getDaysAgo } from "utils/helper";
import useUser from "hooks/userHooks";


interface ExitSurveyTableProps {
  rows: ExitSurveyRow[];
  filters?: ExitSurveyFilter;
  loading?: boolean;
}

export const ExitSurveyTable: React.FC<ExitSurveyTableProps> = ({ rows, loading = false }) => {
  const navigate = useNavigate();
  const { user } = useUser();
  // For now, just return all rows (no filtering logic)
  const filteredRows = rows;

  const handleEmployeeDetails = (employeeId: number) => {
    const profileRoute = getEmployeeProfileRoute(user?.agency_id);
    navigate(profileRoute.replace(":id", employeeId.toString()));
  };

  const getStatusBadgeVariant = (status: ExitSurveyStatus) => {
    switch (status) {
      case ExitSurveyStatus.NEEDS_ATTENTION:
        return "error";
      case ExitSurveyStatus.COMPLETED:
        return "success";
      case ExitSurveyStatus.PENDING_HRBP:
        return "error";
      case ExitSurveyStatus.PENDING_MAIN:
        return "warning";
      case ExitSurveyStatus.PENDING_BOTH:
        return "warning";
      case ExitSurveyStatus.PENDING_EMPLOYEE:
        return "warning";
      default:
        return "default";
    }
  };

  const STATUS_MAP = {
    'pending-hrbp': {
      text: 'Pending',
      subtext: 'HRBP'
    },
    'pending-main': {
      text: 'Pending',
      subtext: 'Both'
    },
    'pending-both': {
      text: 'Pending',
      subtext: 'Both'
    },
    'pending-employee': {
      text: 'Pending',
      subtext: 'Employee'
    },
    'completed': {
      text: 'Completed',
      subtext: ''
    },
    'needs-attention': {
      text: 'Needs Attention',
      subtext: ''
    }
  }

  // Helper function to extract exit date from tenure
  const getExitDate = (tenure: string) => {
    const parts = tenure.split(' to ');
    if (parts.length === 2) {
      return parts[1];
    }
    return tenure;
  };

  // Helper function to get supervisor data
  const getSupervisorData = (assignee: string) => {
    return {
      name: assignee || 'Unassigned',
      icon: assignee ? 'FeatherUser' : 'FeatherUserX' as any,
      color: assignee ? 'text-primary' : 'text-subtext-color'
    };
  };

  if (loading) {
    return <ExitSurveyTableSkeleton />;
  }

  return (
    <div className="w-full overflow-x-auto">
      <Table className="table-fixed w-full">
        <TableHead>
          <TableRow isHeader>
            <TableHeader className="w-auto">Exit Date</TableHeader>
            <TableHeader className="w-auto">Employee</TableHeader>
            <TableHeader className="w-auto">Tenure</TableHeader>
            <TableHeader className="w-auto">Supervisor</TableHeader>
            <TableHeader className="w-auto">Exit Type</TableHeader>
            <TableHeader className="w-auto">Status</TableHeader>
            <TableHeader className="w-auto">Action</TableHeader>
          </TableRow>
        </TableHead>
      <TableBody>
        {filteredRows.map((row, index) => {
          const supervisorData = getSupervisorData(row.supervisor);

          return (
            <TableRow key={`${row.employee_info.employee_id}-${index}`} className="cursor-pointer hover:bg-gray-50" onClick={() => {
              navigate(RETENTION_ROUTES.EXIT_SURVEY_DETAIL.replace(":id", row.employee_info.employee_id.toString()));
            }}>
              <TableCol>
                <span className="whitespace-nowrap text-body font-body text-default-font">
                  {row.exit_date}
                </span>
              </TableCol>
              <TableCol>
                <div className="flex min-h-[48px] flex-col items-start justify-center space-y-1">
                  <span className="text-body-bold font-body-bold text-default-font truncate max-w-full">
                    {row.employee_info.name}
                  </span>
                  <div className="flex flex-col sm:flex-row sm:items-start sm:space-x-1 text-xs">
                    <span className="text-label font-label text-subtext-color truncate">
                      {row.employee_info.job_title || 'N/A'}
                    </span>
                    <span className="hidden sm:inline text-label font-label text-subtext-color">,</span>
                    <div className="flex items-center justify-center gap-1 border-b border-dashed border-[#000000ff] pb-0.5 cursor-pointer">
                      <span className="text-label-bold font-label-bold text-primary truncate">
                        {row.employee_info.office_location || 'N/A'}
                      </span>
                      <SubframeCore.Icon
                        className="text-label font-label text-primary flex-shrink-0"
                        name="FeatherMapPin"
                      />
                    </div>
                    <span className="hidden sm:inline text-label font-label text-subtext-color">,</span>
                    <span className="text-label font-label text-subtext-color truncate">
                      {row.employee_info.office_location || 'N/A'}
                    </span>
                  </div>
                </div>
              </TableCol>
              <TableCol>
                <span className="whitespace-nowrap text-body font-body text-default-font">
                  {row.tenure}
                </span>
              </TableCol>
              <TableCol>
                <div className="flex items-center gap-1 pb-0.5 cursor-pointer">
                  <span className={`text-label-bold font-label-bold ${supervisorData.color} truncate`}>
                    {supervisorData.name}
                  </span>
                  <SubframeCore.Icon
                    className={`text-label font-label ${supervisorData.color} flex-shrink-0`}
                    name={supervisorData.icon}
                  />
                </div>
              </TableCol>
              <TableCol>
                <Tag variant="info-tag" className="w-fit text-xs" score={row.exit_type}>
                  {row.exit_type}
                </Tag>
              </TableCol>
              <TableCol>
                <div className="flex flex-col sm:flex-row sm:items-center gap-1">
                  <Badge variant={getStatusBadgeVariant(row.status) as any} className="w-fit text-xs">
                    {STATUS_MAP[row.status as keyof typeof STATUS_MAP]?.text}
                  </Badge>
                  {STATUS_MAP[row.status as keyof typeof STATUS_MAP]?.subtext && (
                    <span className={`text-xs font-body text-${getStatusBadgeVariant(row.status)}-700 truncate`}>
                      - {STATUS_MAP[row.status as keyof typeof STATUS_MAP]?.subtext} ({getDaysAgo(row.survey_sent_date)})
                    </span>
                  )}
                </div>
              </TableCol>
              <TableActionCell
                actions={[
                  {
                    icon: "FeatherFlagTriangleRight",
                    label: "Exit Survey",
                    onClick: (e: React.MouseEvent<HTMLDivElement>) => {
                      e.stopPropagation();
                      navigate(RETENTION_ROUTES.EXIT_SURVEY_DETAIL.replace(":id", row.employee_info.employee_id.toString()));
                    }
                  },
                  {
                    icon: "FeatherUserCircle",
                    label: "Employee details",
                    onClick: (e: React.MouseEvent<HTMLDivElement>) => {
                      e.stopPropagation();
                      handleEmployeeDetails(row.employee_info.employee_id)
                    }
                  }
                ]}
              />
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
    </div>
  );
}; 